import { useCallback } from 'react';

import { useCheckoutContext } from '@zooplus/checkout-state';
import {
  trackEvent as trackEventUsingLibrary,
  TRACKING_EVENT,
  TrackingData,
  TrackingPage,
} from '@zooplus/tracker-listener';

import { logError } from '@services/sentry-service';
import { CHECKOUT_STATE_PROJECT_ID } from '@utilities/constants';
import {
  extractCheckoutDetails,
  getCommonTrackingData,
  getFormattedExperiments,
} from '@utilities/data-tracking';

const useExperimentsTracking = () => {
  const { checkoutState } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);

  const checkoutDetails = checkoutState ? extractCheckoutDetails(checkoutState) : null;
  const { sid, id, guest, tnc } = checkoutDetails || {};

  const experiments = checkoutState?.togglesAndExperiments;
  const formattedExperiments = experiments && getFormattedExperiments(experiments);

  const trackExperimentsUsingTrackerListener = useCallback(
    (page: TrackingPage) => {
      if (!checkoutState || !formattedExperiments?.length) {
        return;
      }
      try {
        trackEventUsingLibrary({
          page,
          event: TRACKING_EVENT.EXPERIMENT,
          detail: {
            sid,
            ...getCommonTrackingData(),
            customer: {
              id,
              tnc,
              guest,
            },
            experiments: formattedExperiments,
          },
        } as TrackingData);
      } catch (err) {
        logError(
          new Error(
            `An error occurred while trying to send interaction event via @zooplus/tracker-listener: ${err}`,
          ),
        );
      }
    },
    [checkoutState, sid, id, guest, tnc, formattedExperiments],
  );

  return {
    trackExperimentsUsingTrackerListener,
  };
};

export default useExperimentsTracking;
